import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

// Types
export interface DateRange {
  start_date: string;
  end_date: string;
}

export interface SalesReportSummary {
  total_markets: number;
  total_sessions: number;
  total_bets: number;
  total_amount: number;
  total_winning: number;
  net_profit: number;
}

export interface SalesReportData {
  sl_no: number;
  market_id: number;
  market_name: string;
  session: string;
  game_display: string;
  time: string;
  total_bets: number;
  distributed_amount: string;
  date: string;
  winning_amount: string;
}

export interface SalesReportApiResponse {
  success: boolean;
  date_range: DateRange;
  summary: SalesReportSummary;
  data: SalesReportData[];
}

// Types for Sales Report Detail
export interface SalesReportDetailMarket {
  id: number;
  name: string;
  session: string;
  date: string;
  market_type?: string;
}

export interface SalesReportDetailStatistics {
  total_bets: number;
  total_amount: number;
  total_payout: number;
  net_profit: number;
}

export interface SalesReportDetailPagination {
  page: number;
  page_size: number;
  total_pages: number;
  total_records: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface SalesReportDetailBet {
  bet_id: number;
  user_id: number;
  user_name: string;
  user_mobile: string;
  bet_type: string;
  amount: number;
  bet_status: string;
  potential_payout: number;
  created_at: string;
  open_pana: string | null;
  open_digit: string | null;
  close_pana: string | null;
  close_digit: string | null;
  session: string;

  // Optional fields returned for Starline bets.
  selected_number?: string | null;
  session_date?: string | null;
  session_time?: string | null;
  actual_payout?: number;
}

export interface SalesReportDetailApiResponse {
  success: boolean;
  market: SalesReportDetailMarket;
  statistics: SalesReportDetailStatistics;
  pagination: SalesReportDetailPagination;
  bets: SalesReportDetailBet[];
}

export interface SalesReportState {
  data: SalesReportData[];
  summary: SalesReportSummary | null;
  dateRange: DateRange | null;
  loading: boolean;
  error: string | null;
  // Detail state
  detail: SalesReportDetailApiResponse | null;
  detailLoading: boolean;
  detailError: string | null;
}

const initialState: SalesReportState = {
  data: [],
  summary: null,
  dateRange: null,
  loading: false,
  error: null,
  detail: null,
  detailLoading: false,
  detailError: null,
};

// Async thunk to fetch sales report
export const fetchSalesReport = createAsyncThunk(
  'salesReport/fetchSalesReport',
  async (params: {
    // Single date parameter
    date?: string;
    // Date range parameters (alternative format 1)
    start_date?: string;
    end_date?: string;
    // Date range parameters (alternative format 2)
    date_from?: string;
    date_to?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const queryParams: any = {};
      
      // Priority: If single date is provided, use it
      if (params.date) {
        queryParams.date = params.date;
      } 
      // If start_date and end_date are provided, use them
      else if (params.start_date && params.end_date) {
        queryParams.start_date = params.start_date;
        queryParams.end_date = params.end_date;
      }
      // If date_from and date_to are provided, use them
      else if (params.date_from && params.date_to) {
        queryParams.date_from = params.date_from;
        queryParams.date_to = params.date_to;
      }
      // If only one of the range params is provided, still use it
      else {
        if (params.start_date) queryParams.start_date = params.start_date;
        if (params.end_date) queryParams.end_date = params.end_date;
        if (params.date_from) queryParams.date_from = params.date_from;
        if (params.date_to) queryParams.date_to = params.date_to;
      }

      const response = await api.get('/reports/sales/', { params: queryParams });
      console.log('Sales Report API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Sales Report API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to fetch sales report detail
export const fetchSalesReportDetail = createAsyncThunk(
  'salesReport/fetchSalesReportDetail',
  async (params: {
    market_id: number;
    session: string;
    date: string;
    date_from?: string;
    date_to?: string;
    all_time?: boolean;
    page?: number;
    page_size?: number;
  }, { rejectWithValue }) => {
    try {
      const queryParams: any = {
        market_id: params.market_id,
        session: params.session,
        date: params.date,
      };
      
      if (params.date_from) queryParams.date_from = params.date_from;
      if (params.date_to) queryParams.date_to = params.date_to;
      if (params.all_time) queryParams.all_time = true;
      if (params.page) queryParams.page = params.page;
      if (params.page_size) queryParams.page_size = params.page_size;

      const response = await api.get('/reports/sales/detail/', { params: queryParams });
      console.log('Sales Report Detail API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Sales Report Detail API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.response?.data?.message || error.message);
    }
  }
);

const salesReportSlice = createSlice({
  name: 'salesReport',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDetailError: (state) => {
      state.detailError = null;
    },
    resetState: (state) => {
      state.loading = false;
      state.error = null;
      state.data = [];
      state.summary = null;
      state.dateRange = null;
    },
    resetDetailState: (state) => {
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch sales report
    builder
      .addCase(fetchSalesReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesReport.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as SalesReportApiResponse;
        state.data = response.data || [];
        state.summary = response.summary || null;
        state.dateRange = response.date_range || null;
      })
      .addCase(fetchSalesReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch sales report detail
      .addCase(fetchSalesReportDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchSalesReportDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        const response = action.payload as SalesReportDetailApiResponse;
        state.detail = response;
      })
      .addCase(fetchSalesReportDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload as string;
      });
  },
});

export const { clearError, clearDetailError, resetState, resetDetailState } = salesReportSlice.actions;
export default salesReportSlice.reducer;

